import { toast } from 'sonner';
import type { MaterialLibrary } from '@estimator/shared';
import { useMaterialClipboard } from '@/stores/materialClipboardStore';
import { applyMaterialToAll, countPanels, type PanelFraming } from '@/utils/panel-materials';

/**
 * Copy / paste-to-all wiring for one framing editor.
 *
 * Paste reaches BOTH panel lists, which means it can overwrite rows scrolled
 * out of view — hence the undo action on the toast. The previous framing
 * object is captured whole, so undo is a single onChange.
 */
export function usePanelClipboard<F extends PanelFraming>(
  framing: F,
  materials: MaterialLibrary[],
  onChange: (next: F) => void,
) {
  const copy = useMaterialClipboard((s) => s.copy);

  const nameOf = (materialId: string) =>
    materials.find((m) => m.id === materialId)?.name ?? 'Material';

  const copyMaterial = (materialId: string | null) => {
    if (!materialId) return;
    const name = nameOf(materialId);
    copy({ id: materialId, name });
    toast.success(`Copied ${name}`);
  };

  const pasteToAll = (materialId: string) => {
    const previous = framing;
    const count = countPanels(framing);
    if (count === 0) return;
    onChange(applyMaterialToAll(framing, materialId));
    toast.success(`Applied ${nameOf(materialId)} to ${count} panel${count === 1 ? '' : 's'}`, {
      action: { label: 'Undo', onClick: () => onChange(previous) },
    });
  };

  return { copyMaterial, pasteToAll, panelCount: countPanels(framing) };
}
